/*
 * Erika Morón — header behaviour.
 *   <em-header>       sticky/solid state, height variables, dropdowns, cart count + cart drawer trigger state
 *   <em-menu-drawer>  sliding navigation drawer with nested panels
 */

const docEl = document.documentElement;
const SOLID_AFTER = 200;
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const isFinePointer = () => window.matchMedia('(pointer: fine)').matches;

/** Horizon scrolls inside .page-wrapper on wide screens and on the window elsewhere. */
function currentScrollTop() {
  const wrapper = document.querySelector('.page-wrapper');
  const wrapperTop = wrapper ? wrapper.scrollTop : 0;
  return Math.max(window.scrollY || 0, wrapperTop);
}

function focusableIn(root) {
  return Array.from(
    root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])')
  ).filter((el) => el.getClientRects().length > 0);
}

class EmHeader extends HTMLElement {
  #abort = null;
  #resizeObserver = null;
  #mutationObserver = null;
  #lastScroll = 0;
  #visible = true;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    docEl.style.setProperty('--em-header-is-sticky', this.hasAttribute('data-sticky') ? '1' : '0');

    this.#resizeObserver = new ResizeObserver(() => this.#publishHeight());
    this.#resizeObserver.observe(this);
    this.#publishHeight();

    // Re-evaluate the transparent state whenever a dropdown / drawer opens or closes.
    this.#mutationObserver = new MutationObserver(() => this.#updateTransparency());
    this.#mutationObserver.observe(this, { subtree: true, attributes: true, attributeFilter: ['data-open'] });

    const refresh = () => requestAnimationFrame(() => this.#updateTransparency());
    document.addEventListener('shopify:section:load', refresh, { signal });
    document.addEventListener('shopify:section:unload', refresh, { signal });
    document.addEventListener('shopify:section:reorder', refresh, { signal });

    document.addEventListener('scroll', this.#onScroll, { signal, passive: true, capture: true });
    if (this.hasAttribute('data-hide-on-scroll')) {
      window.addEventListener('pointermove', this.#onPointerMove, { signal, passive: true });
    }
    this.#onScroll();

    this.#setupDropdowns(signal);
    this.#setupCart(signal);

    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    this.#setVisible(true);
  }

  /* ---------- Layout variables ---------- */

  #publishHeight() {
    const height = `${Math.round(this.getBoundingClientRect().height)}px`;
    docEl.style.setProperty('--em-header-height', height);
    docEl.style.setProperty('--header-height', height);
    // Horizon declares a --header-height fallback in its own stylesheet; an inline value on body wins.
    document.body.style.setProperty('--header-height', height);
    this.#publishStickyOffset();
  }

  /** --em-sticky-offset: px occupied at the top of the viewport by sticky chrome (sticky bar + visible sticky header). */
  #publishStickyOffset() {
    const rootStyle = getComputedStyle(docEl);
    const barSticky = parseFloat(rootStyle.getPropertyValue('--em-announcement-bar-is-sticky')) || 0;
    const barHeight = parseFloat(rootStyle.getPropertyValue('--em-announcement-bar-height')) || 0;
    const headerHeight = this.hasAttribute('data-sticky') && this.#visible ? this.getBoundingClientRect().height : 0;
    docEl.style.setProperty('--em-sticky-offset', `${Math.round(barSticky * barHeight + headerHeight)}px`);
  }

  /* ---------- Transparent header ---------- */

  /**
   * Contract with template sections: the header overlays the first section of #MainContent when that section
   * contains [data-em-transparent-header] (and no other header-group section follows the header). While it is
   * see-through the bar carries [data-em-header-transparent]; otherwise it has .is-solid.
   */
  #updateTransparency() {
    const firstSection = document.querySelector('#MainContent > .shopify-section:first-child');
    const followedInGroup = Boolean(this.closest('.shopify-section')?.nextElementSibling?.classList.contains('shopify-section'));
    const allowed = Boolean(firstSection?.querySelector('[data-em-transparent-header]')) && !followedInGroup;
    const primary = Boolean(firstSection?.querySelector('[data-em-transparent-header-primary]'));
    const open = Boolean(this.querySelector('[data-open]'));
    const transparent = allowed && !open && currentScrollTop() <= SOLID_AFTER;

    this.toggleAttribute('data-em-header-transparent', transparent);
    this.toggleAttribute('data-em-header-primary', transparent && primary);
    this.classList.toggle('is-solid', !transparent);
  }

  /* ---------- Scroll: solid state + hide on scroll ---------- */

  #onScroll = () => {
    const top = currentScrollTop();
    this.#updateTransparency();

    if (!this.hasAttribute('data-hide-on-scroll')) return;

    if (top > this.#lastScroll && top - this.#lastScroll > 100) {
      this.#lastScroll = top;
      this.#setVisible(false);
    } else if (top < this.#lastScroll) {
      this.#lastScroll = top;
      this.#setVisible(true);
    }
  };

  #onPointerMove = (event) => {
    if (event.clientY < 100 && isFinePointer()) {
      this.#lastScroll = 0;
      this.#setVisible(true);
    }
  };

  #setVisible(visible) {
    if (visible === this.#visible) return;
    if (!visible && this.querySelector('[data-open]')) return;
    this.#visible = visible;
    docEl.style.setProperty('--em-header-is-visible', visible ? '1' : '0');
    this.classList.toggle('is-hidden', !visible);
    this.#publishStickyOffset();
  }

  /* ---------- Dropdowns & mega menus ---------- */

  #setupDropdowns(signal) {
    this.querySelectorAll('[data-em-dropdown-trigger]').forEach((trigger) => {
      const panel = this.#panelFor(trigger);
      const item = trigger.parentElement;
      if (!panel || !item) return;

      if (trigger.hasAttribute('data-open-on-hover')) {
        item.addEventListener(
          'pointerenter',
          (event) => {
            if (event.pointerType === 'mouse') this.#open(trigger);
          },
          { signal }
        );
        item.addEventListener(
          'pointerleave',
          (event) => {
            if (event.pointerType === 'mouse') this.#close(trigger);
          },
          { signal }
        );
      }

      trigger.addEventListener(
        'click',
        (event) => {
          const hoverMode = trigger.hasAttribute('data-open-on-hover');
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';

          // Click mode toggles; in hover mode a tap on touch devices opens the panel first.
          if (!hoverMode || (!isOpen && event.pointerType !== 'mouse' && !isFinePointer())) {
            event.preventDefault();
            isOpen ? this.#close(trigger) : this.#open(trigger);
          }
        },
        { signal }
      );

      trigger.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'ArrowDown' || event.key === ' ') {
            event.preventDefault();
            this.#open(trigger);
            focusableIn(panel)[0]?.focus();
          }
        },
        { signal }
      );

      item.addEventListener(
        'focusout',
        (event) => {
          if (!item.contains(/** @type {Node} */ (event.relatedTarget))) this.#close(trigger);
        },
        { signal }
      );
    });

    document.addEventListener(
      'click',
      (event) => {
        if (!this.contains(/** @type {Node} */ (event.target))) this.#closeAll();
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        const openTrigger = Array.from(this.querySelectorAll('[data-em-dropdown-trigger][aria-expanded="true"]')).pop();
        if (openTrigger) {
          this.#close(openTrigger);
          openTrigger.focus();
        }
      },
      { signal }
    );
  }

  #panelFor(trigger) {
    const id = trigger.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  #open(trigger) {
    const panel = this.#panelFor(trigger);
    if (!panel || panel.hasAttribute('data-open')) return;

    // Close sibling panels of the same level.
    const list = trigger.closest('ul');
    list?.querySelectorAll(':scope > li > [data-em-dropdown-trigger][aria-expanded="true"]').forEach((other) => {
      if (other !== trigger) this.#close(other);
    });

    panel.setAttribute('data-open', '');
    trigger.setAttribute('aria-expanded', 'true');
  }

  #close(trigger) {
    const panel = this.#panelFor(trigger);
    if (!panel) return;
    panel.querySelectorAll('[data-em-dropdown-trigger][aria-expanded="true"]').forEach((nested) => this.#close(nested));
    panel.removeAttribute('data-open');
    trigger.setAttribute('aria-expanded', 'false');
  }

  #closeAll() {
    this.querySelectorAll('.em-header__menu > li > [data-em-dropdown-trigger][aria-expanded="true"]').forEach((trigger) =>
      this.#close(trigger)
    );
  }

  #onBlockSelect = (event) => {
    const panel = /** @type {HTMLElement} */ (event.target);
    if (!this.contains(panel) || !panel.matches?.('[data-em-dropdown]')) return;
    const trigger = this.querySelector(`[aria-controls="${panel.id}"]`);
    if (trigger) this.#open(trigger);
  };

  #onBlockDeselect = (event) => {
    const panel = /** @type {HTMLElement} */ (event.target);
    if (!this.contains(panel) || !panel.matches?.('[data-em-dropdown]')) return;
    const trigger = this.querySelector(`[aria-controls="${panel.id}"]`);
    if (trigger) this.#close(trigger);
  };

  /* ---------- Cart (Horizon cart drawer integration) ---------- */

  #setupCart(signal) {
    import('@shopify/events')
      .then(({ StandardEvents }) => {
        if (signal.aborted || !StandardEvents?.cartLinesUpdate) return;
        document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, { signal });
      })
      .catch(() => {});

    window.addEventListener(
      'pageshow',
      (event) => {
        if (event.persisted) this.#refreshCartCount();
      },
      { signal }
    );

    document.addEventListener('theme-drawer:open', this.#syncCartTrigger, { signal });
    document.addEventListener('theme-drawer:close', this.#syncCartTrigger, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });
    this.#syncCartTrigger();
  }

  #onCartLinesUpdate = (event) => {
    event.promise
      ?.then((result) => {
        const count = result?.cart?.totalQuantity ?? result?.detail?.itemCount;
        if (typeof count === 'number') this.#renderCartCount(count);
      })
      .catch(() => {});
  };

  async #refreshCartCount() {
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      this.#renderCartCount(cart.item_count);
    } catch (error) {
      /* network errors keep the server-rendered count */
    }
  }

  #renderCartCount(count) {
    this.querySelectorAll('[data-em-cart-dot]').forEach((dot) => {
      dot.hidden = count === 0;
    });
    this.querySelectorAll('[data-em-cart-count]').forEach((node) => {
      node.textContent = String(count);
    });
  }

  #syncCartTrigger = () => {
    const drawer = document.getElementById('cart-drawer');
    const open = Boolean(drawer?.hasAttribute('open'));
    this.querySelectorAll('[data-em-cart-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  };

  /**
   * On wide screens Horizon opens the cart drawer as a non-modal panel (no backdrop click handling).
   * The Stretch look has a dimmed overlay (assets/em-cart-drawer.css), so a click on it closes the drawer.
   */
  #onDocumentClick = (event) => {
    const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
    if (!drawer?.hasAttribute('open') || !window.matchMedia('(min-width: 990px)').matches) return;

    const path = event.composedPath();
    const insideDrawer = path.some((node) => node instanceof Element && node.closest?.('#cart-drawer dialog, dialog[open], [aria-controls="cart-drawer"]'));
    if (insideDrawer || !event.target?.isConnected) return;

    drawer.close?.();
  };
}

class EmMenuDrawer extends HTMLElement {
  #abort = null;
  #busy = false;
  #returnFocus = null;
  #lockedScroll = false;

  get isOpen() {
    return this.hasAttribute('data-open');
  }

  get #content() {
    return this.querySelector('.em-menu-drawer__content');
  }

  get #overlay() {
    return this.querySelector('.em-menu-drawer__overlay');
  }

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    document.addEventListener(
      'click',
      (event) => {
        const toggle = /** @type {HTMLElement} */ (event.target).closest?.(`[aria-controls="${this.id}"]`);
        if (!toggle) return;
        event.preventDefault();
        this.isOpen ? this.close() : this.open(toggle);
      },
      { signal }
    );

    this.addEventListener(
      'click',
      (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        if (target.closest('[data-em-drawer-close]')) {
          this.close();
          return;
        }
        const panelToggle = target.closest('[data-panel-toggle]');
        if (panelToggle) this.#switchTo(panelToggle.getAttribute('aria-controls'));
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (!this.isOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        } else if (event.key === 'Tab') {
          this.#trapTab(event);
        }
      },
      { signal }
    );

    document.addEventListener(
      'shopify:section:unload',
      () => {
        if (this.isOpen) this.#finishClose();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    if (this.#lockedScroll) docEl.removeAttribute('scroll-lock');
  }

  #toggles() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
  }

  #visiblePanel() {
    return this.querySelector('.em-menu-drawer__panel.is-visible');
  }

  async open(trigger) {
    if (this.isOpen || this.#busy) return;
    this.#busy = true;
    this.#returnFocus = trigger ?? /** @type {HTMLElement} */ (document.activeElement);

    const header = this.closest('em-header') ?? this.parentElement;
    this.style.setProperty('--em-menu-offset-top', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);

    this.querySelectorAll('.em-menu-drawer__panel').forEach((panel) => {
      panel.classList.toggle('is-visible', panel.hasAttribute('data-main-panel'));
      panel.style.cssText = '';
    });

    this.hidden = false;
    this.setAttribute('data-open', '');
    this.#toggles().forEach((toggle) => toggle.setAttribute('aria-expanded', 'true'));

    if (!docEl.hasAttribute('scroll-lock')) {
      docEl.setAttribute('scroll-lock', '');
      this.#lockedScroll = true;
    }

    const content = this.#content;
    const mainPanel = this.querySelector('[data-main-panel]');
    const animations = [this.#overlay.animate({ opacity: [0, 1] }, { duration: 200 })];

    if (prefersReducedMotion()) {
      animations.push(content.animate({ opacity: [0, 1] }, { duration: 200 }));
    } else {
      animations.push(
        content.animate({ transform: ['translateX(-100%)', 'translateX(0)'] }, { duration: 350, easing: 'cubic-bezier(0.2, 0.4, 0.2, 1)' })
      );
      const items = Array.from(mainPanel.querySelectorAll('.em-menu-drawer__body > *'));
      items.forEach((item, index) => {
        item.animate(
          { opacity: [0, 1], transform: ['translateY(1em)', 'translateY(0)'] },
          { duration: 200, delay: 400 + index * 100, fill: 'backwards' }
        );
      });
      const footer = mainPanel.querySelector('.em-menu-drawer__footer');
      footer?.animate(
        { opacity: [0, 1], transform: ['translateY(1em)', 'translateY(0)'] },
        { duration: 200, delay: 400 + Math.max(0, items.length - 1) * 100 + 100, easing: 'ease-in-out', fill: 'backwards' }
      );
    }

    content.focus({ preventScroll: true });
    await Promise.allSettled(animations.map((animation) => animation.finished));
    this.#busy = false;
  }

  async close() {
    if (!this.isOpen || this.#busy) return;
    this.#busy = true;

    const content = this.#content;
    const animations = [this.#overlay.animate({ opacity: [1, 0] }, { duration: 250, fill: 'forwards' })];

    if (prefersReducedMotion()) {
      animations.push(content.animate({ opacity: [1, 0] }, { duration: 200, fill: 'forwards' }));
    } else {
      const panel = this.#visiblePanel();
      if (panel) animations.push(panel.animate({ opacity: [1, 0] }, { duration: 150, easing: 'ease-in-out', fill: 'forwards' }));
      animations.push(
        content.animate(
          { transform: ['translateX(0)', 'translateX(-100%)'] },
          { duration: 250, easing: 'cubic-bezier(0.645, 0.045, 0.355, 1)', fill: 'forwards' }
        )
      );
    }

    await Promise.allSettled(animations.map((animation) => animation.finished));
    animations.forEach((animation) => animation.cancel());
    this.#finishClose();
  }

  #finishClose() {
    this.hidden = true;
    this.removeAttribute('data-open');
    this.#toggles().forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
    this.querySelectorAll('[data-panel-toggle][aria-expanded]').forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
    this.querySelectorAll('.em-menu-drawer__panel').forEach((panel) => {
      panel.classList.toggle('is-visible', panel.hasAttribute('data-main-panel'));
      panel.style.cssText = '';
    });

    if (this.#lockedScroll) {
      docEl.removeAttribute('scroll-lock');
      this.#lockedScroll = false;
    }

    this.#busy = false;
    this.#returnFocus?.focus?.({ preventScroll: true });
  }

  async #switchTo(panelId) {
    if (this.#busy || !panelId) return;
    const from = this.#visiblePanel();
    const to = this.querySelector(`#${CSS.escape(panelId)}`);
    if (!from || !to || from === to) return;

    this.#busy = true;
    to.classList.add('is-visible');

    const toMain = to.hasAttribute('data-main-panel');
    const rtl = document.dir === 'rtl' ? -1 : 1;
    let animations;

    if (prefersReducedMotion()) {
      animations = [to.animate({ opacity: [0, 1] }, { duration: 200 }), from.animate({ opacity: [1, 0] }, { duration: 200, fill: 'forwards' })];
    } else if (toMain) {
      animations = [
        from.animate({ transform: ['translateX(0%)', `translateX(${100 * rtl}%)`] }, { duration: 250, easing: 'ease-in-out', fill: 'forwards' }),
        to.animate(
          { backgroundColor: ['rgb(0 0 0 / 0.15)', 'rgb(0 0 0 / 0)'], transform: [`translateX(${-30 * rtl}%)`, 'translateX(0)'] },
          { duration: 250, easing: 'ease-in-out' }
        ),
      ];
    } else {
      animations = [
        from.animate(
          { backgroundColor: ['rgb(0 0 0 / 0)', 'rgb(0 0 0 / 0.15)'], transform: ['translateX(0)', `translateX(${-30 * rtl}%)`] },
          { duration: 400, easing: 'cubic-bezier(0.645, 0.045, 0.355, 1)', fill: 'forwards' }
        ),
        to.animate({ transform: [`translateX(${100 * rtl}%)`, 'translateX(0)'] }, { duration: 400, easing: 'cubic-bezier(0.645, 0.045, 0.355, 1)' }),
      ];
    }

    await Promise.allSettled(animations.map((animation) => animation.finished));
    animations.forEach((animation) => animation.cancel());
    from.classList.remove('is-visible');

    this.querySelectorAll(`[data-panel-toggle][aria-controls="${panelId}"]`).forEach((toggle) => {
      if (!toMain) toggle.setAttribute('aria-expanded', 'true');
    });
    if (toMain) {
      this.querySelectorAll('[data-main-panel] [data-panel-toggle]').forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
    }

    focusableIn(to)[0]?.focus({ preventScroll: true });
    this.#busy = false;
  }

  #trapTab(event) {
    const panel = this.#visiblePanel();
    const toggle = this.#toggles().find((element) => element.getClientRects().length > 0);
    const focusables = [...(toggle ? [toggle] : []), ...(panel ? focusableIn(panel) : [])];
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    const inside = focusables.includes(/** @type {HTMLElement} */ (active)) || this.#content.contains(active);

    if (!inside) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && (active === first || active === this.#content)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('em-header')) {
  customElements.define('em-header', EmHeader);
}

if (!customElements.get('em-menu-drawer')) {
  customElements.define('em-menu-drawer', EmMenuDrawer);
}
